import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Plus, Users, Trash2, Calendar as CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roles = [
  { value: 'MANAGER', label: 'Manager', icon: '👔' },
  { value: 'FRONT_DESK', label: 'Front Desk', icon: '💼' },
  { value: 'HOUSEKEEPING', label: 'Housekeeping', icon: '🧹' },
  { value: 'MAINTENANCE', label: 'Maintenance', icon: '🔧' },
];

const Staff = () => {
  const { selectedProperty } = useOutletContext();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'FRONT_DESK',
    phone: '',
    hourly_rate: 0
  });

  useEffect(() => {
    if (selectedProperty) {
      fetchStaff();
    }
  }, [selectedProperty]);

  const fetchStaff = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/staff/property/${selectedProperty.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaff(response.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API}/staff/property/${selectedProperty.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Staff member added');
      setDialogOpen(false);
      setFormData({ name: '', email: '', role: 'FRONT_DESK', phone: '', hourly_rate: 0 });
      fetchStaff();
    } catch (error) {
      toast.error('Failed to add staff');
    }
  };

  const handleToggleActive = async (staffId, currentActive) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(`${API}/staff/${staffId}?is_active=${!currentActive}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Status updated');
      fetchStaff();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (staffId) => {
    if (!confirm('Delete this staff member?')) return;
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/staff/${staffId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Staff member deleted');
      fetchStaff();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getRoleInfo = (role) => roles.find(r => r.value === role) || roles[0];

  if (!selectedProperty) return <div className="flex items-center justify-center h-full"><p className="text-slate-600">Please select a property</p></div>;
  if (loading) return <div className="flex items-center justify-center h-full">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="staff-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Staff Management</h1>
          <p className="text-slate-600 mt-1">Manage employees and schedules</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-staff-button">
              <Plus size={18} />
              Add Staff
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Staff Member</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateStaff} className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Role *</Label>
                <Select value={formData.role} onValueChange={(val) => setFormData({...formData, role: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(r => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.icon} {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
              <div>
                <Label>Hourly Rate</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({...formData, hourly_rate: parseFloat(e.target.value)})}
                />
              </div>
              <Button type="submit" className="w-full">
                Add Staff Member
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {staff.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Users size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 mb-4">No staff members yet.</p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus size={18} className="mr-2" />
              Add First Staff Member
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {staff.map((member) => {
            const roleInfo = getRoleInfo(member.role);
            return (
              <Card key={member.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{roleInfo.icon}</span>
                      <span>{member.name}</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(member.id)}>
                      <Trash2 size={16} className="text-red-500" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-sm text-slate-600">Role</p>
                    <p className="font-medium">{roleInfo.label}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Email</p>
                    <p className="text-sm">{member.email}</p>
                  </div>
                  {member.phone && (
                    <div>
                      <p className="text-sm text-slate-600">Phone</p>
                      <p className="text-sm">{member.phone}</p>
                    </div>
                  )}
                  {member.hourly_rate > 0 && (
                    <div>
                      <p className="text-sm text-slate-600">Hourly Rate</p>
                      <p className="font-semibold text-cyan-600">${member.hourly_rate}/hr</p>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-3 border-t">
                    <Label className="text-sm">Active</Label>
                    <Switch
                      checked={member.is_active}
                      onCheckedChange={() => handleToggleActive(member.id, member.is_active)}
                    />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Staff;
